import axios from "axios";
import {
  API_ERROR_CODES,
  isApiErrorEnvelope,
  type ApiErrorEnvelope,
  type ApiFieldError,
} from "@/shared/types/apiError";

/**
 * Coerce any caught error into an ApiErrorEnvelope. Handles four cases:
 *   1. AxiosError with an envelope-shaped body → pass through
 *   2. AxiosError with a legacy ApiResponse body → synthesize envelope
 *   3. AxiosError with no response (network failure) → NETWORK_ERROR envelope
 *   4. Anything else (raw Error, throw of a string, etc.) → INTERNAL_ERROR envelope
 *
 * The legacy fallback path is kept active during the controller sweep — once all
 * controllers emit envelopes, only branches (1), (3), and (4) should fire in practice.
 */
export const normalizeError = (err: unknown): ApiErrorEnvelope => {
  if (axios.isAxiosError(err)) {
    const response = err.response;
    const data = response?.data;

    const envelope = coerceEnvelope(data, response?.status ?? 0);
    if (envelope) {
      return envelope;
    }

    // No response at all → network/CORS failure / aborted / timeout.
    if (!response) {
      return {
        code: API_ERROR_CODES.NetworkError,
        userMessage: "Couldn't reach the server.",
        nextStep: "Check your connection and try again.",
        fieldErrors: [],
        traceId: "",
        httpStatus: 0,
      };
    }

    // Legacy fallback: synthesize an envelope from whatever shape arrived so the
    // UI surfaces something sensible until the endpoint is migrated.
    return synthesizeFromLegacyShape(data, response.status);
  }

  // Non-Axios error reached the boundary — defensive fallback.
  return {
    code: API_ERROR_CODES.InternalError,
    userMessage: "Something on our end didn't work.",
    nextStep: "Please try again.",
    fieldErrors: [],
    traceId: "",
    httpStatus: 500,
  };
};

const synthesizeFromLegacyShape = (data: unknown, httpStatus: number): ApiErrorEnvelope => {
  const userMessage =
    extractLegacyMessage(data) ?? "Something went wrong.";
  const fieldErrors = extractLegacyFieldErrors(data);

  return {
    code: API_ERROR_CODES.LegacyError,
    userMessage,
    nextStep: null,
    fieldErrors,
    traceId: "",
    httpStatus,
  };
};

const coerceEnvelope = (data: unknown, httpStatus: number): ApiErrorEnvelope | null => {
  if (!data || typeof data !== "object") return null;

  if (isApiErrorEnvelope(data)) {
    return data;
  }

  const obj = data as Record<string, unknown>;
  const code = typeof obj.code === "string" ? obj.code : null;
  const userMessage =
    typeof obj.userMessage === "string"
      ? obj.userMessage
      : typeof obj.message === "string"
        ? obj.message
        : typeof obj.title === "string"
          ? obj.title
          : null;

  if (!code || !userMessage) return null;

  const fieldErrors = normalizeFieldErrors(obj.fieldErrors);

  return {
    code,
    userMessage,
    nextStep: typeof obj.nextStep === "string" ? obj.nextStep : null,
    fieldErrors: fieldErrors.length > 0 ? fieldErrors : extractLegacyFieldErrors(data),
    traceId: typeof obj.traceId === "string" ? obj.traceId : "",
    httpStatus: typeof obj.httpStatus === "number" ? obj.httpStatus : httpStatus,
    data: obj.data,
  };
};

const extractLegacyMessage = (data: unknown): string | null => {
  if (typeof data === "string") return data;
  if (!data || typeof data !== "object") return null;
  const obj = data as Record<string, unknown>;
  if (typeof obj.message === "string") return obj.message;
  if (typeof obj.title === "string") return obj.title;
  if (Array.isArray(obj.errors) && obj.errors.length > 0 && typeof obj.errors[0] === "string") {
    return obj.errors[0];
  }
  return null;
};

const extractLegacyFieldErrors = (data: unknown): ApiFieldError[] => {
  if (!data || typeof data !== "object") return [];
  const obj = data as Record<string, unknown>;

  const envelopeFieldErrors = normalizeFieldErrors(obj.fieldErrors);
  if (envelopeFieldErrors.length > 0) {
    return envelopeFieldErrors;
  }

  // .NET legacy: object map { FieldName: ["error1", "error2"] }
  if (obj.errors && typeof obj.errors === "object" && !Array.isArray(obj.errors)) {
    const result: ApiFieldError[] = [];
    for (const [field, value] of Object.entries(obj.errors)) {
      const camelField = toCamelFirst(field);
      if (Array.isArray(value)) {
        for (const msg of value) {
          if (typeof msg === "string") {
            result.push({ field: camelField, code: "INVALID_VALUE", userMessage: msg });
          }
        }
      } else if (typeof value === "string") {
        result.push({ field: camelField, code: "INVALID_VALUE", userMessage: value });
      }
    }
    return result;
  }

  // .NET legacy: string array ["FieldName: error message", ...]
  if (Array.isArray(obj.errors)) {
    const result: ApiFieldError[] = [];
    for (const item of obj.errors) {
      if (typeof item !== "string") continue;
      const colonIdx = item.indexOf(":");
      if (colonIdx > 0) {
        const field = toCamelFirst(item.substring(0, colonIdx).trim());
        const userMessage = item.substring(colonIdx + 1).trim();
        result.push({ field, code: "INVALID_VALUE", userMessage });
      }
    }
    return result;
  }

  return [];
};

const normalizeFieldErrors = (value: unknown): ApiFieldError[] => {
  if (!value) return [];

  if (Array.isArray(value)) {
    const result: ApiFieldError[] = [];
    for (const item of value) {
      if (typeof item === "string") {
        result.push({ field: "_form", code: "INVALID_VALUE", userMessage: item });
        continue;
      }
      if (!item || typeof item !== "object") continue;
      const obj = item as Record<string, unknown>;
      const field = typeof obj.field === "string" ? toCamelPath(obj.field) : "_form";
      const code = typeof obj.code === "string" ? obj.code : "INVALID_VALUE";
      const message =
        typeof obj.userMessage === "string"
          ? obj.userMessage
          : typeof obj.message === "string"
            ? obj.message
            : typeof obj.errorMessage === "string"
              ? obj.errorMessage
              : null;

      if (message) {
        result.push({ field, code, userMessage: message });
      }
    }
    return result;
  }

  if (typeof value === "object") {
    const result: ApiFieldError[] = [];
    for (const [field, messages] of Object.entries(value as Record<string, unknown>)) {
      const normalizedField = toCamelPath(field);
      if (Array.isArray(messages)) {
        for (const message of messages) {
          if (typeof message === "string") {
            result.push({ field: normalizedField, code: "INVALID_VALUE", userMessage: message });
          }
        }
      } else if (typeof messages === "string") {
        result.push({ field: normalizedField, code: "INVALID_VALUE", userMessage: messages });
      }
    }
    return result;
  }

  return [];
};

const toCamelFirst = (s: string): string => {
  if (!s) return s;
  return s.charAt(0).toLowerCase() + s.slice(1);
};

const toCamelPath = (path: string): string =>
  path
    .split(".")
    .map((segment) => toCamelFirst(segment))
    .join(".");
